// features/removals/removalsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchRemovalsAPI,
  fetchDealerRemovalsAPI,
  restoreClientAPI,
  getUninstallationFormAPI,
  getCSVLogsAPI,
  getTextLogsAPI
} from './removalsAPI';

// ==================== ASYNC THUNKS ====================

// Fetch all removals (Admin/Superadmin)
export const fetchRemovals = createAsyncThunk(
  'removals/fetchRemovals',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchRemovalsAPI();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch removals');
    }
  }
);

// Fetch dealer removals
export const fetchDealerRemovals = createAsyncThunk(
  'removals/fetchDealerRemovals',
  async (dealerId, { rejectWithValue }) => {
    try {
      const data = await fetchDealerRemovalsAPI(dealerId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch dealer removals');
    }
  }
);

// Restore client from removals
export const restoreClient = createAsyncThunk(
  'removals/restoreClient',
  async (clientId, { dispatch, rejectWithValue }) => {
    try {
      const result = await restoreClientAPI(clientId);
      
      // After successful restore, refresh the removals list
      const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
      if (user.role === "Admin" || user.role === "superadmin") {
        await dispatch(fetchRemovals());
      } else {
        await dispatch(fetchDealerRemovals(user.dealerId));
      }
      
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to restore client');
    }
  }
);

// Get uninstallation form PDF
export const getUninstallationForm = createAsyncThunk(
  'removals/getUninstallationForm',
  async (clientId, { rejectWithValue }) => {
    try {
      const blob = await getUninstallationFormAPI(clientId);
      return blob;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch uninstallation form');
    }
  }
);

// Get CSV logs
export const getCSVLogs = createAsyncThunk(
  'removals/getCSVLogs',
  async ({ cbid, clientName }, { rejectWithValue }) => {
    try {
      const data = await getCSVLogsAPI(cbid, clientName);
      return { data, clientName };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch CSV logs');
    }
  }
);

// Get text logs
export const getTextLogs = createAsyncThunk(
  'removals/getTextLogs',
  async ({ cbid, clientName }, { rejectWithValue }) => {
    try {
      const data = await getTextLogsAPI(cbid, clientName);
      return { data, clientName };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch text logs');
    }
  }
);

// ==================== SLICE ====================

const removalsSlice = createSlice({
  name: 'removals',
  initialState: {
    removals: [],
    loading: false,
    error: null,
    successMessage: null,
    totalRecords: 0,
    // Logs state
    csvLogs: null,
    textLogs: null,
    logsLoading: false,
    logsError: null,
    // PDF state
    pdfBlob: null,
    pdfUrl: null,
    pdfLoading: false,
    pdfError: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.successMessage = null;
    },
    clearLogs: (state) => {
      state.csvLogs = null;
      state.textLogs = null;
      state.logsError = null;
    },
    clearPDF: (state) => {
      if (state.pdfUrl) {
        URL.revokeObjectURL(state.pdfUrl);
      }
      state.pdfBlob = null;
      state.pdfUrl = null;
      state.pdfError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== FETCH REMOVALS ==========
      .addCase(fetchRemovals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRemovals.fulfilled, (state, action) => {
        state.loading = false;
        state.removals = action.payload;
        state.totalRecords = action.payload?.length || 0;
      })
      .addCase(fetchRemovals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.removals = [];
        state.totalRecords = 0;
      })
      
      // ========== FETCH DEALER REMOVALS ==========
      .addCase(fetchDealerRemovals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealerRemovals.fulfilled, (state, action) => {
        state.loading = false;
        state.removals = action.payload;
        state.totalRecords = action.payload?.length || 0;
      })
      .addCase(fetchDealerRemovals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.removals = [];
        state.totalRecords = 0;
      })
      
      // ========== RESTORE CLIENT ==========
      .addCase(restoreClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreClient.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Client restored successfully!';
      })
      .addCase(restoreClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // ========== GET UNINSTALLATION FORM ==========
      .addCase(getUninstallationForm.pending, (state) => {
        state.pdfLoading = true;
        state.pdfError = null;
      })
      .addCase(getUninstallationForm.fulfilled, (state, action) => {
        state.pdfLoading = false;
        state.pdfBlob = action.payload;
        state.pdfUrl = URL.createObjectURL(action.payload);
      })
      .addCase(getUninstallationForm.rejected, (state, action) => {
        state.pdfLoading = false;
        state.pdfError = action.payload;
      })
      
      // ========== GET CSV LOGS ==========
      .addCase(getCSVLogs.pending, (state) => {
        state.logsLoading = true;
        state.logsError = null;
      })
      .addCase(getCSVLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.csvLogs = action.payload;
      })
      .addCase(getCSVLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.logsError = action.payload;
      })
      
      // ========== GET TEXT LOGS ==========
      .addCase(getTextLogs.pending, (state) => {
        state.logsLoading = true;
        state.logsError = null;
      })
      .addCase(getTextLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.textLogs = action.payload;
      })
      .addCase(getTextLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.logsError = action.payload;
      });
  }
});

// ==================== SELECTORS ====================
export const selectRemovals = (state) => state.removals.removals;
export const selectRemovalsLoading = (state) => state.removals.loading;
export const selectRemovalsError = (state) => state.removals.error;
export const selectRemovalsSuccess = (state) => state.removals.successMessage;
export const selectTotalRemovals = (state) => state.removals.totalRecords;
export const selectCSVLogs = (state) => state.removals.csvLogs;
export const selectTextLogs = (state) => state.removals.textLogs;
export const selectLogsLoading = (state) => state.removals.logsLoading;
export const selectLogsError = (state) => state.removals.logsError;
export const selectPDFBlob = (state) => state.removals.pdfBlob;
export const selectPDFUrl = (state) => state.removals.pdfUrl;
export const selectPDFLoading = (state) => state.removals.pdfLoading;
export const selectPDFError = (state) => state.removals.pdfError;

export const { clearError, clearSuccess, clearLogs, clearPDF } = removalsSlice.actions;

export default removalsSlice.reducer;
